/* eslint-disable unicorn/prefer-ternary */
/* eslint-disable @typescript-eslint/dot-notation */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { createId } from "@paralleldrive/cuid2";
import {
  defaultAward,
  defaultCertification,
  defaultEducation,
  defaultExperience,
  defaultInterest,
  defaultLanguage,
  defaultProfile,
  defaultProject,
  defaultPublication,
  defaultReference,
  defaultResumeData,
  defaultSkill,
  defaultVolunteer,
} from "@reactive-resume/schema";
import { Json } from "@reactive-resume/utils";
import { Schema } from "zod";

import { Parser } from "../interfaces/parser";
import { JsonResume, jsonResumeSchema } from "./schema";

export * from "./schema";

export class JsonResumeParser implements Parser<Json, JsonResume> {
  schema: Schema;

  constructor() {
    this.schema = jsonResumeSchema;
  }

  // 辅助函数：验证和格式化 URL
  private validateAndFormatUrl(url: string | undefined | null): string {
    if (!url || url.trim() === "") return "";

    try {
      // 检查 URL 是否有协议前缀，如果没有则添加 https://
      const urlString = url.trim();
      let validUrl = urlString;

      if (urlString && !/^https?:\/\//i.test(urlString)) {
        validUrl = `https://${urlString}`;
      }

      // 验证 URL 是否有效
      new URL(validUrl);
      return validUrl;
    } catch {
      // URL 无效，返回默认URL
      return "https://nova-cv.com";
    }
  }

  // 辅助函数：验证和格式化邮箱
  private validateAndFormatEmail(email: string | undefined | null): string {
    if (!email) return "example@mail.com";

    const emailString = email.trim();
    // 简单的邮箱格式验证
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (emailRegex.test(emailString)) {
      return emailString;
    } else {
      // 邮箱无效，返回默认邮箱
      return "example@mail.com";
    }
  }

  readFile(file: File): Promise<Json> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      // eslint-disable-next-line unicorn/prefer-add-event-listener
      reader.onload = () => {
        try {
          const result = JSON.parse(reader.result as string) as Json;
          resolve(result);
        } catch {
          reject(new Error("Failed to parse JSON"));
        }
      };

      // eslint-disable-next-line unicorn/prefer-add-event-listener
      reader.onerror = () => {
        reject(new Error("Failed to read the file"));
      };

      // eslint-disable-next-line unicorn/prefer-blob-reading-methods
      reader.readAsText(file);
    });
  }

  validate(data: Json) {
    console.log("开始验证数据:", data);

    // 1. 先尝试完整验证
    const result = this.schema.safeParse(data);
    if (result.success) {
      console.log("完整验证成功");
      return result.data as JsonResume;
    }

    console.log("完整验证失败，开始部分验证");
    const partialResult: Partial<JsonResume> = {};

    // 2. 处理基础信息
    if (data["basics"]) {
      try {
        const basicsSchema = (this.schema as any)._def.shape().basics;
        const basicsResult = basicsSchema.safeParse(data["basics"]);
        console.log("basics 验证结果:", basicsResult);
        if (basicsResult.success) {
          partialResult.basics = basicsResult.data;
        } else {
          // 如果验证失败，保留原始数据
          partialResult.basics = data["basics"];
        }
      } catch (error) {
        console.error("basics 验证出错:", error);
        partialResult.basics = data["basics"];
      }
    }

    // 3. 处理数组类型的字段
    const arrayFields = [
      "work",
      "volunteer",
      "education",
      "awards",
      "certificates",
      "publications",
      "skills",
      "languages",
      "interests",
      "references",
      "projects",
    ] as const;

    for (const field of arrayFields) {
      if (Array.isArray(data[field])) {
        try {
          const fieldSchema = (this.schema as any)._def.shape()[field];
          if (fieldSchema) {
            // 收集所有项，包括验证失败的
            const validItems = data[field].map((item) => {
              const parseResult = fieldSchema.safeParse(item);
              return parseResult.success ? parseResult.data : item;
            });

            if (validItems.length > 0) {
              partialResult[field] = validItems;
            }
            console.log(`${field} 字段验证结果:`, validItems);
          } else {
            // 如果没有对应的 schema，保留原始数据
            partialResult[field] = data[field];
          }
        } catch (error) {
          console.error(`${field} 字段验证出错:`, error);
          partialResult[field] = data[field];
        }
      }
    }

    console.log("部分验证后的结果:", partialResult);
    return partialResult as JsonResume;
  }

  convert(data: JsonResume) {
    const result = JSON.parse(JSON.stringify(defaultResumeData));

    // Basics
    result.basics.name = data.basics?.name ?? "";
    result.basics.headline = data.basics?.label ?? "";
    result.basics.picture.url = data.basics?.image ?? "";
    result.basics.email = this.validateAndFormatEmail(data.basics?.email);
    result.basics.phone = data.basics?.phone ?? "";
    result.basics.location = data.basics?.location?.address ?? "";
    result.basics.url.href = this.validateAndFormatUrl(data.basics?.url);
    result.sections.summary.content = data.basics?.summary ?? "";

    // Profiles
    if (data.basics?.profiles) {
      for (const profile of data.basics.profiles) {
        result.sections.profiles.items.push({
          ...defaultProfile,
          id: createId(),
          icon: profile.network?.toLocaleLowerCase() ?? "",
          network: profile.network ?? "",
          username: profile.username ?? "",
          url: { ...defaultProfile.url, href: this.validateAndFormatUrl(profile.url) },
        });
      }
    }

    // Work
    if (data.work) {
      for (const work of data.work) {
        // 处理日期，避免出现 "undefined - undefined" 的情况
        let dateString = "";
        if (work.startDate || work.endDate) {
          dateString = `${work.startDate ?? ""} - ${work.endDate ?? ""}`;
        }

        // 处理 highlights，将其与 summary 合并
        let combinedSummary = work.summary ?? "";

        // 如果存在 highlights，将其添加到 summary 中
        if (work.highlights && work.highlights.length > 0) {
          // 如果 summary 不为空，添加一个换行
          if (combinedSummary) {
            combinedSummary += "<br><br>";
          }

          // 将 highlights 作为列表添加到 summary 中，使用 HTML 标签确保换行
          combinedSummary +=
            "<ul>" + work.highlights.map((highlight) => `<li>${highlight}</li>`).join("") + "</ul>";
        }

        result.sections.experience.items.push({
          ...defaultExperience,
          id: createId(),
          company: work.name ?? "",
          position: work.position ?? "",
          summary: combinedSummary,
          date: dateString,
          url: { ...defaultExperience.url, href: this.validateAndFormatUrl(work.url) },
        });
      }
    }

    // Volunteer
    if (data.volunteer) {
      for (const volunteer of data.volunteer) {
        // 处理日期，避免出现 "undefined - undefined" 的情况
        let dateString = "";
        if (volunteer.startDate || volunteer.endDate) {
          dateString = `${volunteer.startDate ?? ""} - ${volunteer.endDate ?? ""}`;
        }

        // 处理 highlights，将其与 summary 合并
        let combinedSummary = volunteer.summary ?? "";

        // 如果存在 highlights，将其添加到 summary 中
        if (volunteer.highlights && volunteer.highlights.length > 0) {
          // 如果 summary 不为空，添加一个换行
          if (combinedSummary) {
            combinedSummary += "<br><br>";
          }

          // 将 highlights 作为列表添加到 summary 中，使用 HTML 标签确保换行
          combinedSummary +=
            "<ul>" +
            volunteer.highlights.map((highlight) => `<li>${highlight}</li>`).join("") +
            "</ul>";
        }

        result.sections.volunteer.items.push({
          ...defaultVolunteer,
          id: createId(),
          organization: volunteer.organization ?? "",
          date: dateString,
          position: volunteer.position ?? "",
          summary: combinedSummary,
          url: { ...defaultVolunteer.url, href: this.validateAndFormatUrl(volunteer.url) },
        });
      }
    }

    // Education
    if (data.education) {
      for (const education of data.education) {
        // 处理日期，避免出现 "undefined - undefined" 的情况
        let dateString = "";
        if (education.startDate || education.endDate) {
          dateString = `${education.startDate ?? ""} - ${education.endDate ?? ""}`;
        }

        result.sections.education.items.push({
          ...defaultEducation,
          id: createId(),
          institution: education.institution ?? "",
          studyType: education.studyType ?? "",
          area: education.area ?? "",
          score: education.score ?? "",
          date: dateString,
          url: { ...defaultEducation.url, href: this.validateAndFormatUrl(education.url) },
        });
      }
    }

    // Awards
    if (data.awards) {
      for (const award of data.awards) {
        result.sections.awards.items.push({
          ...defaultAward,
          id: createId(),
          title: award.title ?? "",
          date: award.date ?? "",
          awarder: award.awarder ?? "",
          summary: award.summary ?? "",
        });
      }
    }

    // Certificates
    if (data.certificates) {
      for (const certificate of data.certificates) {
        result.sections.certifications.items.push({
          ...defaultCertification,
          id: createId(),
          name: certificate.name ?? "",
          date: certificate.date ?? "",
          issuer: certificate.issuer ?? "",
          summary: certificate.summary ?? "",
        });
      }
    }

    // Publications
    if (data.publications) {
      for (const publication of data.publications) {
        result.sections.publications.items.push({
          ...defaultPublication,
          id: createId(),
          name: publication.name ?? "",
          publisher: publication.publisher ?? "",
          summary: publication.summary ?? "",
          date: publication.releaseDate ?? "",
          url: { ...defaultPublication.url, href: this.validateAndFormatUrl(publication.url) },
        });
      }
    }

    // Skills
    if (data.skills) {
      for (const skill of data.skills) {
        result.sections.skills.items.push({
          ...defaultSkill,
          id: createId(),
          name: skill.name ?? "",
          description: skill.level ?? "",
          keywords: skill.keywords ?? [],
        });
      }
    }

    // Languages
    if (data.languages) {
      for (const language of data.languages) {
        result.sections.languages.items.push({
          ...defaultLanguage,
          id: createId(),
          name: language.language ?? "",
          description: language.fluency ?? "",
        });
      }
    }

    // Interests
    if (data.interests) {
      for (const interest of data.interests) {
        result.sections.interests.items.push({
          ...defaultInterest,
          id: createId(),
          name: interest.name ?? "",
          keywords: interest.keywords ?? [],
        });
      }
    }

    // References
    if (data.references) {
      for (const reference of data.references) {
        result.sections.references.items.push({
          ...defaultReference,
          id: createId(),
          name: reference.name ?? "",
          summary: reference.reference ?? "",
        });
      }
    }

    // Projects
    if (data.projects) {
      for (const project of data.projects) {
        // 处理 highlights，将其与 summary 合并
        let combinedSummary = project.summary ?? "";

        // 如果存在 highlights，将其添加到 summary 中
        if (project.highlights && project.highlights.length > 0) {
          // 如果 summary 不为空，添加一个换行
          if (combinedSummary) {
            combinedSummary += "<br><br>";
          }

          // 将 highlights 作为列表添加到 summary 中，使用 HTML 标签确保换行
          combinedSummary +=
            "<ul>" +
            project.highlights.map((highlight) => `<li>${highlight}</li>`).join("") +
            "</ul>";
        }

        // 处理日期，避免出现 "undefined - undefined" 的情况
        let dateString = "";
        if (project.startDate || project.endDate) {
          dateString = `${project.startDate ?? ""} - ${project.endDate ?? ""}`;
        }

        result.sections.projects.items.push({
          ...defaultProject,
          id: createId(),
          name: project.name ?? "",
          description: project.description ?? "",
          date: dateString,
          summary: combinedSummary,
          url: { ...defaultProject.url, href: this.validateAndFormatUrl(project.url) },
        });
      }
    }

    return result;
  }
}
